import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { shippingApi } from '../services/api';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ArchiveBoxIcon,
} from '@heroicons/react/24/outline';

interface PackagePreset {
  id: string;
  name: string;
  length: number;
  width: number;
  height: number;
  weight?: number;
  is_default?: boolean;
}

interface PresetFormData {
  name: string;
  length: string;
  width: string;
  height: string;
  weight: string;
}

const emptyForm: PresetFormData = { name: '', length: '', width: '', height: '', weight: '' };

export const PackageSettings: React.FC = () => {
  const [presets, setPresets] = useState<PackagePreset[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<PresetFormData>(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchPresets = useCallback(async () => {
    try {
      setLoading(true);
      const data = await shippingApi.getPackagePresets();
      setPresets(data.presets || []);
    } catch (err) {
      console.error('Failed to load package presets:', err);
      toast.error('Failed to load package presets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPresets();
  }, [fetchPresets]);

  const openAddForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEditForm = (preset: PackagePreset) => {
    setEditingId(preset.id);
    setForm({
      name: preset.name,
      length: String(preset.length),
      width: String(preset.width),
      height: String(preset.height),
      weight: preset.weight ? String(preset.weight) : '',
    });
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const validateForm = (): string | null => {
    if (!form.name.trim()) return 'Name is required';
    if (!form.length || Number(form.length) <= 0) return 'Length must be greater than 0';
    if (!form.width || Number(form.width) <= 0) return 'Width must be greater than 0';
    if (!form.height || Number(form.height) <= 0) return 'Height must be greater than 0';
    if (form.weight && Number(form.weight) < 0) return 'Weight cannot be negative';
    return null;
  };

  const handleSave = async () => {
    const error = validateForm();
    if (error) {
      toast.error(error);
      return;
    }

    setSaving(true);
    try {
      const presetData = {
        name: form.name.trim(),
        length: Number(form.length),
        width: Number(form.width),
        height: Number(form.height),
        weight: form.weight ? Number(form.weight) : 0,
      };

      if (editingId) {
        // Update: save all presets with the edited one replaced
        const updated = presets.map(p =>
          p.id === editingId ? { ...p, ...presetData } : p
        );
        await shippingApi.savePackagePresets(updated);
        toast.success('Package preset updated');
      } else {
        // Add new
        await shippingApi.addPackagePreset(presetData);
        toast.success('Package preset added');
      }

      await fetchPresets();
      cancelForm();
    } catch (err) {
      console.error('Failed to save package preset:', err);
      toast.error('Failed to save package preset');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (preset: PackagePreset) => {
    if (!window.confirm(`Delete "${preset.name}"?`)) return;
    try {
      await shippingApi.deletePackagePreset(preset.id);
      toast.success('Package preset deleted');
      await fetchPresets();
    } catch (err) {
      console.error('Failed to delete package preset:', err);
      toast.error('Failed to delete package preset');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-blue"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Add button */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">
          Define box sizes and weights for quick selection when creating shipments.
        </p>
        <button
          onClick={openAddForm}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-brand-blue rounded-lg hover:bg-blue-700 transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          Add Box Size
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-xl border-2 border-brand-blue shadow-subtle p-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">
            {editingId ? 'Edit Box Size' : 'New Box Size'}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Name */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">Name</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Small Box"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-blue focus:border-transparent"
              />
            </div>

            {/* Length */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Length (in)</label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={form.length}
                onChange={e => setForm(f => ({ ...f, length: e.target.value }))}
                placeholder="0"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-blue focus:border-transparent"
              />
            </div>

            {/* Width */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Width (in)</label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={form.width}
                onChange={e => setForm(f => ({ ...f, width: e.target.value }))}
                placeholder="0"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-blue focus:border-transparent"
              />
            </div>

            {/* Height */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Height (in)</label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={form.height}
                onChange={e => setForm(f => ({ ...f, height: e.target.value }))}
                placeholder="0"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-blue focus:border-transparent"
              />
            </div>

            {/* Weight */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Box Weight (oz)</label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={form.weight}
                onChange={e => setForm(f => ({ ...f, weight: e.target.value }))}
                placeholder="0"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-brand-blue focus:border-transparent"
              />
              <p className="text-xs text-slate-400 mt-1">Weight of the empty box (added to product weight)</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 mt-6">
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-brand-blue rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Saving...' : editingId ? 'Update' : 'Add Box Size'}
            </button>
            <button
              onClick={cancelForm}
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Presets List */}
      {presets.length === 0 ? (
        <div className="bg-white rounded-xl border-2 border-dashed border-slate-200 p-12 text-center">
          <ArchiveBoxIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No box sizes configured yet.</p>
          <p className="text-xs text-slate-400 mt-1">Add your first box size to get started.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border-2 border-slate-200 shadow-subtle overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Dimensions (in)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Box Weight (oz)</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {[...presets].sort((a, b) => a.length - b.length || a.width - b.width || a.height - b.height).map((preset) => (
                <tr key={preset.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                        <ArchiveBoxIcon className="w-4 h-4 text-brand-blue" />
                      </div>
                      <span className="text-sm font-medium text-slate-900">{preset.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {preset.length} x {preset.width} x {preset.height}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {preset.weight ? `${preset.weight} oz` : '—'}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEditForm(preset)}
                        className="p-1.5 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <PencilSquareIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(preset)}
                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
